// ─────────────────────────────────────────────────────────────────────────────
// Guarda de "cambios sin guardar" para navegaciones que desmontan la pantalla actual.
//
// Por qué a mano y no con `useBlocker` de react-router: la app monta un solo `<BrowserRouter>`
// (no un data router `createBrowserRouter`), y `useBlocker` SOLO funciona con data router. Así que
// la guarda vive en un contexto propio con dos piezas:
//   1. `beforeunload` mientras haya algo sucio → cubre recargar, cerrar pestaña y navegación DURA
//      (window.location.assign / deep-links externos).
//   2. `guardNavigation(proceed)` → para la navegación INTERNA de la SPA (hoy la usa la campana):
//      si hay cambios sin guardar, pide confirmación con `ConfirmModal` antes de ejecutar `proceed`.
//
// Los formularios se registran con `useUnsavedChangesGuard(isDirty)`. Fuera de un provider (p. ej.
// la misma campana montada en el portal Pulso) el contexto cae a un no-op: `guardNavigation` ejecuta
// directo y `setDirty` no hace nada, para no obligar a montar el provider en toda la app.
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { sinBase } from '@/utils/basePath';

interface UnsavedChangesApi {
  /** Registra/actualiza si un formulario (identificado por `id`) tiene cambios sin guardar. */
  setDirty: (id: string, dirty: boolean) => void;
  /** Ejecuta `proceed`; si hay cambios sin guardar, pide confirmación antes. */
  guardNavigation: (proceed: () => void) => void;
}

const NOOP: UnsavedChangesApi = { setDirty: () => {}, guardNavigation: (proceed) => proceed() };
const UnsavedChangesContext = createContext<UnsavedChangesApi>(NOOP);

export function UnsavedChangesProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { t } = useTranslation('recognition');
  const navigate = useNavigate();
  const location = useLocation();
  const dirtyIds = useRef<Set<string>>(new Set());
  const [hasUnsaved, setHasUnsaved] = useState(false);
  // Acción de navegación pendiente de confirmación. Se guarda envuelta (`() => fn`) porque el
  // updater de useState ejecutaría la función si se pasara pelada.
  const [pending, setPending] = useState<(() => void) | null>(null);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    const set = dirtyIds.current;
    if (dirty) set.add(id);
    else set.delete(id);
    setHasUnsaved(set.size > 0);
  }, []);

  const guardNavigation = useCallback((proceed: () => void) => {
    if (dirtyIds.current.size === 0) {
      proceed();
      return;
    }
    setPending(() => proceed);
  }, []);

  // beforeunload: recarga / cierre de pestaña / navegación dura. Solo activo mientras haya algo sucio.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // Intercepción CENTRAL de la navegación SPA por enlaces (menú lateral, MobileNav, cualquier
  // <a href="/…"> interno). Solo se engancha mientras hay algo sucio, así que no cuesta nada en el
  // caso normal y no altera el comportamiento nativo (nueva pestaña con Ctrl/⌘/click medio, externos,
  // descargas y anclas siguen intactos). En fase de captura para adelantarse al handler de NavLink.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      const target = anchor.getAttribute('target');
      if (!href || href.startsWith('//') || !href.startsWith('/')) return; // solo rutas internas
      if (target === '_blank' || anchor.hasAttribute('download')) return;
      // 🔴 El href RENDERIZADO por <Link>/<NavLink> ya trae el prefijo del ambiente (lo pone el
      // `basename`), mientras que `location.pathname` viene SIN él. Hay que quitarlo antes de
      // comparar y antes de navegar: si no, en el ambiente de desarrollo la comparación de abajo
      // nunca se cumple y `navigate(href)` vuelve a anteponer el prefijo -> '/dev/dev/app/...'.
      // En QA y PROD `sinBase` es la identidad, así que no cambia nada.
      const destino = sinBase(href);
      if (destino === location.pathname + location.search) return; // misma ruta: nada que perder
      e.preventDefault();
      guardNavigation(() => navigate(destino));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [hasUnsaved, guardNavigation, navigate, location.pathname, location.search]);

  const api = useMemo(() => ({ setDirty, guardNavigation }), [setDirty, guardNavigation]);

  return (
    <UnsavedChangesContext.Provider value={api}>
      {children}
      {pending && (
        <ConfirmModal
          type="warning"
          title={t('notifications.unsavedTitle')}
          message={t('notifications.unsavedDesc')}
          confirmText={t('notifications.unsavedConfirm')}
          cancelText={t('notifications.unsavedCancel')}
          onConfirm={pending}
          onClose={() => setPending(null)}
        />
      )}
    </UnsavedChangesContext.Provider>
  );
}

/** Registra un formulario como "sucio" mientras `isDirty` sea true; lo limpia al desmontar. */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const { setDirty } = useContext(UnsavedChangesContext);
  const id = useId();
  useEffect(() => {
    setDirty(id, isDirty);
    return () => setDirty(id, false);
  }, [id, isDirty, setDirty]);
}

/** Devuelve `guardNavigation` para envolver una navegación que desmontaría la pantalla actual. */
export function useNavigationGuard() {
  return useContext(UnsavedChangesContext).guardNavigation;
}
